/**
 * Dear Diary Theme - Margin Notes & Annotations
 * Phase 3: Side notes, yanamas messages, sticky notes
 */

import React, { useState } from 'react'
import { useTheme } from '@/components/providers/ThemeProvider'
import { X } from 'lucide-react'

// ============================================
// MARGIN NOTE (Side annotation)
// ============================================
interface MarginNoteProps {
  children: React.ReactNode
  icon?: string
  position?: 'left' | 'right'
  color?: 'yellow' | 'pink' | 'blue' | 'green'
  dismissible?: boolean
  className?: string
}

export const MarginNote: React.FC<MarginNoteProps> = ({
  children,
  icon = '📝',
  position = 'right',
  color = 'yellow',
  dismissible = false,
  className = ''
}) => {
  const { isDiary } = useTheme()
  const [dismissed, setDismissed] = useState(false)

  if (!isDiary || dismissed) return null

  const colors = {
    yellow: { bg: 'var(--sticky-note)', border: 'var(--accent-gold)' },
    pink: { bg: 'var(--washi-tape-pink)', border: '#E8B4B8' },
    blue: { bg: 'var(--washi-tape-blue)', border: '#A8C5E8' },
    green: { bg: 'var(--washi-tape-green)', border: '#A8E8B8' },
  }

  const c = colors[color]
  const positionStyle = position === 'right' 
    ? { right: '-200px', marginLeft: 'auto' }
    : { left: '-200px', marginRight: 'auto' }

  return (
    <div
      className={`absolute w-44 p-3 diary-sticky-wobble ${className}`}
      style={{
        ...positionStyle,
        top: '0',
        background: c.bg,
        borderLeft: position === 'right' ? `3px solid ${c.border}` : undefined,
        borderRight: position === 'left' ? `3px solid ${c.border}` : undefined,
        fontFamily: "var(--font-handwritten, 'Kalam', cursive)",
        fontSize: '14px',
        color: 'var(--ink-sepia)',
        transform: `rotate(${position === 'right' ? 2 : -2}deg)`,
        boxShadow: '2px 2px 8px rgba(0,0,0,0.1)',
        zIndex: 10,
      }}
    >
      {dismissible && (
        <button
          onClick={() => setDismissed(true)}
          className="absolute top-1 right-1 p-1 hover:opacity-70"
          style={{ color: 'var(--ink-faded)' }}
        >
          <X size={14} />
        </button>
      )}
      <span
        className="absolute"
        style={{
          top: '-12px',
          [position === 'right' ? 'left' : 'right']: '-8px',
          fontSize: '18px',
        }}
      >
        {icon}
      </span>
      <div className={dismissible ? 'pr-4' : ''}>{children}</div>
    </div>
  )
}

// ============================================
// HORTON NOTE (yanamas' speech bubble)
// ============================================
interface HortonNoteProps {
  children: React.ReactNode
  variant?: 'bubble' | 'sticky' | 'margin'
  mood?: 'happy' | 'encouraging' | 'thinking' | 'celebration'
  dismissible?: boolean
  className?: string
}

export const HortonNote: React.FC<HortonNoteProps> = ({
  children,
  variant = 'bubble',
  mood = 'happy',
  dismissible = true,
  className = ''
}) => {
  const { isDiary } = useTheme()
  const [dismissed, setDismissed] = useState(false)

  if (!isDiary || dismissed) return null

  const moodEmojis = {
    happy: '🐘',
    encouraging: '💪🐘',
    thinking: '🤔🐘',
    celebration: '🎉🐘',
  }

  const moodColors = {
    happy: 'var(--washi-tape-blue)',
    encouraging: 'var(--washi-tape-green)',
    thinking: 'var(--highlight-yellow)',
    celebration: 'var(--washi-tape-pink)',
  }

  if (variant === 'bubble') {
    return (
      <div
        className={`relative p-4 max-w-xs diary-page-enter ${className}`}
        style={{
          background: moodColors[mood],
          borderRadius: '16px',
          fontFamily: "var(--font-handwritten, 'Kalam', cursive)",
          fontSize: '15px',
          color: 'var(--ink-black)',
          boxShadow: '2px 2px 10px rgba(0,0,0,0.08)',
        }}
      >
        {dismissible && (
          <button
            onClick={() => setDismissed(true)}
            className="absolute top-2 right-2 p-1 hover:opacity-70"
            style={{ color: 'var(--ink-faded)' }}
          >
            <X size={14} />
          </button>
        )}
        <span
          className="absolute"
          style={{ top: '-18px', left: '12px', fontSize: '28px' }}
        >
          {moodEmojis[mood]}
        </span>
        <div className={dismissible ? 'pr-6' : ''} style={{ marginTop: '4px' }}>
          {children}
        </div>
        {/* Speech bubble tail */}
        <div
          style={{
            position: 'absolute',
            bottom: '-10px',
            left: '24px',
            width: 0,
            height: 0,
            borderLeft: '10px solid transparent',
            borderRight: '10px solid transparent',
            borderTop: `12px solid ${moodColors[mood]}`,
          }}
        />
      </div>
    )
  }

  if (variant === 'sticky') {
    return (
      <div
        className={`relative p-4 diary-sticky-wobble ${className}`}
        style={{
          background: 'var(--sticky-note)',
          width: '200px',
          fontFamily: "var(--font-handwritten, 'Kalam', cursive)",
          fontSize: '14px',
          color: 'var(--ink-sepia)',
          boxShadow: '3px 3px 10px rgba(0,0,0,0.12)',
          transform: 'rotate(-2deg)',
        }}
      >
        {dismissible && (
          <button
            onClick={() => setDismissed(true)}
            className="absolute top-1 right-1 p-1 hover:opacity-70"
            style={{ color: 'var(--ink-faded)' }}
          >
            <X size={14} />
          </button>
        )}
        <span className="block text-lg mb-2">{moodEmojis[mood]}</span>
        <div className={dismissible ? 'pr-4' : ''}>{children}</div>
      </div>
    )
  }

  // margin variant
  return (
    <MarginNote icon={moodEmojis[mood]} color="blue" dismissible={dismissible} className={className}>
      {children}
    </MarginNote>
  )
}

// ============================================
// STICKY NOTE
// ============================================
interface StickyNoteProps {
  children: React.ReactNode
  color?: 'yellow' | 'pink' | 'blue' | 'green' | 'orange'
  rotation?: number
  pinned?: boolean
  className?: string
}

export const StickyNote: React.FC<StickyNoteProps> = ({
  children,
  color = 'yellow',
  rotation = -2,
  pinned = true,
  className = ''
}) => {
  const { isDiary } = useTheme()

  if (!isDiary) {
    return <div className={`p-4 bg-warn/20 rounded ${className}`}>{children}</div>
  }

  const colors = {
    yellow: 'var(--sticky-note)',
    pink: 'var(--sticky-note-pink)',
    blue: 'var(--washi-tape-blue)',
    green: 'var(--washi-tape-green)',
    orange: '#FFE9D9',
  }

  return (
    <div
      className={`relative p-4 diary-sticky-wobble ${className}`}
      style={{
        background: colors[color],
        minWidth: '150px',
        maxWidth: '250px',
        fontFamily: "var(--font-handwritten, 'Kalam', cursive)",
        fontSize: '15px',
        color: 'var(--ink-black)',
        boxShadow: '3px 3px 12px rgba(0,0,0,0.15)',
        transform: `rotate(${rotation}deg)`,
      }}
    >
      {pinned && (
        <div
          className="absolute"
          style={{
            top: '-8px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '20px',
            height: '20px',
            background: 'radial-gradient(circle, var(--bookmark-red) 40%, #9A3A2A 60%)',
            borderRadius: '50%',
            boxShadow: '1px 2px 4px rgba(0,0,0,0.2)',
          }}
        />
      )}
      {children}
    </div>
  )
}

// ============================================
// ANNOTATION HIGHLIGHT
// ============================================
interface AnnotationProps {
  children: React.ReactNode
  type?: 'highlight' | 'underline' | 'circle' | 'crossout'
  color?: 'yellow' | 'pink' | 'green' | 'blue'
  note?: string
  className?: string
}

export const Annotation: React.FC<AnnotationProps> = ({
  children,
  type = 'highlight',
  color = 'yellow',
  note,
  className = ''
}) => {
  const { isDiary } = useTheme()
  const [showNote, setShowNote] = useState(false)

  if (!isDiary) return <span className={className}>{children}</span>

  const highlightColors = {
    yellow: 'var(--highlight-yellow)',
    pink: 'var(--washi-tape-pink)',
    green: 'var(--washi-tape-green)',
    blue: 'var(--washi-tape-blue)',
  }

  const inkColors = {
    yellow: 'var(--accent-gold)',
    pink: 'var(--bookmark-red)',
    green: 'var(--semantic-success)',
    blue: 'var(--ink-blue)',
  }

  const getStyle = (): React.CSSProperties => {
    switch (type) {
      case 'highlight':
        return {
          background: `linear-gradient(to bottom, transparent 60%, ${highlightColors[color]} 60%, ${highlightColors[color]} 90%, transparent 90%)`,
          padding: '0 4px',
        }
      case 'underline':
        return {
          borderBottom: `2px wavy ${inkColors[color]}`,
          paddingBottom: '2px',
        }
      case 'circle':
        return {
          border: `2px solid ${inkColors[color]}`,
          borderRadius: '50%',
          padding: '2px 8px',
          transform: 'rotate(-2deg)',
          display: 'inline-block',
        }
      case 'crossout':
        return {
          position: 'relative',
          color: 'var(--ink-faded)',
        }
      default:
        return {}
    }
  }

  return (
    <span
      className={`relative cursor-pointer ${className}`}
      style={getStyle()}
      onClick={() => note && setShowNote(!showNote)}
      title={note}
    >
      {children}
      {type === 'crossout' && (
        <span
          style={{
            position: 'absolute',
            left: '-5%',
            top: '50%',
            width: '110%',
            height: '2px',
            background: inkColors[color],
            transform: 'rotate(-3deg)',
          }}
        />
      )}
      {note && showNote && (
        <span
          className="absolute left-full ml-2 top-0 p-2 text-sm whitespace-nowrap"
          style={{
            background: highlightColors[color],
            fontFamily: "var(--font-handwritten, 'Kalam', cursive)",
            color: 'var(--ink-sepia)',
            borderRadius: '4px',
            boxShadow: '2px 2px 6px rgba(0,0,0,0.1)',
            zIndex: 20,
          }}
        >
          {note}
        </span>
      )}
    </span>
  )
}

// ============================================
// DIARY DIVIDER
// ============================================
interface DiaryDividerProps {
  variant?: 'line' | 'dots' | 'flourish' | 'doodle'
  className?: string
}

export const DiaryDivider: React.FC<DiaryDividerProps> = ({
  variant = 'line',
  className = ''
}) => {
  const { isDiary } = useTheme()

  if (!isDiary) {
    return <hr className={`border-soft my-4 ${className}`} />
  }

  const renderVariant = () => {
    switch (variant) {
      case 'dots':
        return (
          <div className="flex justify-center items-center gap-2 py-4">
            <span style={{ color: 'var(--ink-faded)', fontSize: '10px' }}>●</span>
            <span style={{ color: 'var(--ink-faded)', fontSize: '10px' }}>●</span>
            <span style={{ color: 'var(--ink-faded)', fontSize: '10px' }}>●</span>
          </div>
        )
      case 'flourish':
        return (
          <div
            className="flex justify-center items-center py-4"
            style={{ fontFamily: "var(--font-handwritten, 'Kalam', cursive)", color: 'var(--ink-faded)', fontSize: '20px' }}
          >
            ～ ❦ ～
          </div>
        )
      case 'doodle':
        return (
          <div className="flex justify-center items-center py-4">
            <svg width="200" height="20" viewBox="0 0 200 20">
              <path
                d="M0,10 Q25,5 50,10 T100,10 T150,10 T200,10"
                fill="none"
                stroke="var(--ink-faded)"
                strokeWidth="1.5"
                strokeDasharray="4,4"
              />
            </svg>
          </div>
        )
      default:
        return (
          <div
            className="my-4"
            style={{
              borderBottom: '1px dashed var(--paper-margin)',
            }}
          />
        )
    }
  }

  return <div className={className}>{renderVariant()}</div>
}

export default {
  MarginNote,
  HortonNote,
  StickyNote,
  Annotation,
  DiaryDivider,
}
